/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import type {
  DataPortSchemaObject,
  FromSchema,
  TypedArray,
  TypedArrayConstructor,
  TypedArraySchemaOptions,
} from "@workglow/util/schema";
import { cosineSimilarity } from "@workglow/util/schema";
import { safeEmit } from "../events/safeEmit";
import { InMemoryTabularStorage } from "../tabular/InMemoryTabularStorage";
import type { AutoGeneratedKeys, InsertEntity } from "../tabular/ITabularStorage";
import { assertVectorShape, validateVectorEntities } from "./assertVectorShape";
import type { IVectorStorage, VectorSearchOptions } from "./IVectorStorage";
import { getMetadataProperty, getVectorProperty, matchesFilter } from "./IVectorStorage";

/** In-memory {@link IVectorStorage} on top of {@link InMemoryTabularStorage}. */
export class InMemoryVectorStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  Metadata extends Record<string, unknown> = Record<string, unknown>,
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  InsertType extends InsertEntity<Entity, AutoGeneratedKeys<Schema>> = InsertEntity<
    Entity,
    AutoGeneratedKeys<Schema>
  >,
>
  extends InMemoryTabularStorage<Schema, PrimaryKeyNames, Entity>
  implements IVectorStorage<Metadata, Schema, Entity, PrimaryKeyNames>
{
  private vectorDimensions: number;
  private vectorPropertyName: keyof Entity;
  private metadataPropertyName: keyof Entity | undefined;

  constructor(
    schema: Schema,
    primaryKeyNames: PrimaryKeyNames,
    indexes: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[] = [],
    dimensions: number,
    _vectorCtor: TypedArrayConstructor = Float32Array
  ) {
    super(schema, primaryKeyNames, indexes);

    this.vectorDimensions = dimensions;

    const vectorProp = getVectorProperty(schema);
    if (!vectorProp) {
      throw new Error("Schema must have a property with type array and format TypedArray");
    }
    this.vectorPropertyName = vectorProp as keyof Entity;
    this.metadataPropertyName = getMetadataProperty(schema) as keyof Entity | undefined;
  }

  getVectorDimensions(): number {
    return this.vectorDimensions;
  }

  /**
   * Per-instance promise-chain mutex. Serializes `put` and `putBulk` so a
   * concurrent single-row `put` cannot slip rows into the live Map while a
   * `putBulk` is mid-flight — otherwise the snapshot/restore on failure would
   * wipe the interleaved row along with the failed batch.
   */
  private mutexChain: Promise<void> = Promise.resolve();
  private async mutex<T>(fn: () => Promise<T>): Promise<T> {
    const prev = this.mutexChain;
    let release!: () => void;
    this.mutexChain = new Promise<void>((resolve) => {
      release = resolve;
    });
    await prev;
    try {
      return await fn();
    } finally {
      release();
    }
  }

  /**
   * Fail-fast vector validation. We mirror the cloud backends here so an
   * in-memory test fixture catches the same shape mismatches a Postgres /
   * SQLite store would reject — otherwise tests pass and the deployment
   * silently breaks on garbage rows. Routed through the same mutex as
   * `putBulk` so concurrent writes serialize and a `put` cannot land in the
   * Map mid-batch only to be wiped by a rollback snapshot.
   */
  override async put(value: InsertType): Promise<Entity> {
    assertVectorShape(
      (value as Record<string, unknown>)[this.vectorPropertyName as string],
      this.vectorDimensions,
      "write"
    );
    return this.mutex(() => super.put(value));
  }

  /**
   * Validate the whole batch up front, then perform an atomic batch put:
   * snapshot mutable state, write rows one at a time, and roll back to the
   * snapshot if any single `put` throws. The inherited default `putBulk`
   * runs writes through `Promise.all`, which can leave the Map (and
   * autoincrement counter) in a half-applied state when the second or later
   * `put` rejects after earlier ones already mutated the Map. This override
   * guarantees either every row in the batch is visible after a successful
   * return, or none of the batch's rows are visible after a thrown error —
   * matching the contract the vector overlay relies on for RAG ingestion
   * replays.
   */
  override async putBulk(values: InsertType[]): Promise<Entity[]> {
    validateVectorEntities(
      values as ReadonlyArray<Record<string, unknown>>,
      this.vectorPropertyName as string,
      this.vectorDimensions
    );
    if (values.length === 0) return [];
    return this.mutex(async () => {
      const snapshot = this.snapshotMutableState();
      const results: Entity[] = [];
      // Track the PKs of rows that actually committed before the throw, so the
      // rollback event lets subscribers surgically invalidate caches instead of
      // re-reading the whole table. Typed via `any` because the public PK type
      // is computed from the schema and BaseTabularStorage holds its own
      // PrimaryKey type parameter — the value still flows through the
      // {@link TabularEventListeners} payload at the emit site below.
      const committedIds: any[] = [];
      try {
        for (const value of values) {
          const committed = await super.put(value);
          results.push(committed);
          // Extract the PK from the committed entity (handles auto-generated
          // keys assigned during put).
          committedIds.push(this.separateKeyValueFromCombined(committed).key);
        }
        return results;
      } catch (error) {
        this.restoreMutableState(snapshot);
        safeEmit(this.events, "rollback", { op: "putBulk", error, ids: committedIds });
        throw error;
      }
    });
  }

  async similaritySearch(
    query: TypedArray,
    options: VectorSearchOptions<Record<string, unknown>> = {}
  ) {
    assertVectorShape(query, this.vectorDimensions, "query");
    const { topK = 10, filter, scoreThreshold = 0 } = options;
    const results: Array<Entity & { score: number }> = [];

    const allEntities = (await this.getAll()) || [];

    for (const entity of allEntities) {
      const vector = entity[this.vectorPropertyName] as TypedArray;
      const metadata = this.metadataPropertyName
        ? (entity[this.metadataPropertyName] as Metadata)
        : ({} as Metadata);

      if (filter && !matchesFilter(metadata, filter)) {
        continue;
      }

      const score = cosineSimilarity(query, vector);

      if (score < scoreThreshold) {
        continue;
      }

      results.push({
        ...entity,
        score,
      } as Entity & { score: number });
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, topK);
  }
}
